export const convertToReadableText = (str: string) => {
  return str
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^[a-z]/, (match) => match.toUpperCase())
    .replace(/\s+[a-z]/g, (match) => match.toUpperCase());
};

/**
 * Text casing options
 */
export type TextCasing = 'sentence' | 'title' | 'lower' | 'upper';

/**
 * Formats text from various formats (UPPER_SNAKE_CASE, camelCase, snake_case, etc.)
 * to a readable format with the specified casing.
 * 
 * @param text - The text to format
 * @param casing - The desired output casing (default: 'sentence')
 * @returns Formatted text
 * 
 * Examples:
 * - formatText('HEADSHOT_WORKFLOW_PIPELINE', 'sentence') => "Headshot workflow pipeline"
 * - formatText('HEADSHOT_WORKFLOW_PIPELINE', 'title') => "Headshot Workflow Pipeline"
 * - formatText('userProfileData', 'sentence') => "User profile data"
 * - formatText('user_profile_data', 'title') => "User Profile Data"
 */
export const formatText = (text: string, casing: TextCasing = 'sentence'): string => {
  if (!text) return '';
  
  // Step 1: Convert to space-separated words
  let words: string[];
  
  // Handle UPPER_SNAKE_CASE
  if (/^[A-Z0-9_]+$/.test(text)) {
    words = text.toLowerCase().split('_');
  } 
  // Handle snake_case
  else if (text.includes('_')) {
    words = text.split('_');
  }
  // Handle camelCase or PascalCase
  else if (/[A-Z]/.test(text)) {
    words = text
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .toLowerCase()
      .split(' ');
  } 
  // Handle other cases (already space-separated or single word)
  else {
    words = text.toLowerCase().split(' ');
  }
  
  // Step 2: Apply the desired casing
  switch (casing) {
    case 'sentence':
      // Capitalize only the first word
      return words.map((word, index) => 
        index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
      ).join(' ');
      
    case 'title':
      // Capitalize each word
      return words.map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
      
    case 'lower':
      // All lowercase
      return words.join(' ');
      
    case 'upper':
      // All uppercase
      return words.join(' ').toUpperCase();
      
    default:
      return words.join(' ');
  }
};
